import CssBaseline from '@mui/material/CssBaseline';
import { ThemeProvider } from '@mui/material/styles';
import { ReactNode, useMemo, useState, useSyncExternalStore } from 'react';
import { generateTheme } from '@/theme';
import { ThemeModeContext, ThemeMode, ResolvedThemeMode } from './ThemeModeContext';

const STORAGE_KEY = 'physna-theme-mode';
const darkMediaQuery = globalThis.matchMedia?.('(prefers-color-scheme: dark)');

const themes = {
  light: generateTheme('light'),
  dark: generateTheme('dark'),
};

function getStoredMode(): ThemeMode {
  const stored = localStorage.getItem(STORAGE_KEY);
  if (stored === 'light' || stored === 'dark' || stored === 'system') {
    return stored;
  }
  return 'system';
}

function subscribeToSystemPreference(callback: () => void) {
  darkMediaQuery?.addEventListener('change', callback);
  return () => darkMediaQuery?.removeEventListener('change', callback);
}

function getSystemPreference(): ResolvedThemeMode {
  return darkMediaQuery?.matches ? 'dark' : 'light';
}

function updateMetaThemeColor(resolvedMode: ResolvedThemeMode) {
  let meta = document.querySelector('meta[name="theme-color"]');
  if (!meta) {
    meta = document.createElement('meta');
    meta.setAttribute('name', 'theme-color');
    document.head.append(meta);
  }
  meta.setAttribute('content', themes[resolvedMode].palette.background.backdrop);
}

export function ThemeModeProvider({ children }: { readonly children: ReactNode }) {
  const [mode, setMode] = useState<ThemeMode>(getStoredMode);
  const systemPreference = useSyncExternalStore(subscribeToSystemPreference, getSystemPreference);

  const resolvedMode: ResolvedThemeMode = mode === 'system' ? systemPreference : mode;

  document.body.dataset.theme = resolvedMode;
  updateMetaThemeColor(resolvedMode);

  function handleSetMode(newMode: ThemeMode) {
    setMode(newMode);
    localStorage.setItem(STORAGE_KEY, newMode);
  }

  const contextValue = useMemo(() => ({ mode, resolvedMode, setMode: handleSetMode }), [mode, resolvedMode]);

  return (
    <ThemeModeContext.Provider value={contextValue}>
      <ThemeProvider theme={themes[resolvedMode]}>
        <CssBaseline enableColorScheme />
        {children}
      </ThemeProvider>
    </ThemeModeContext.Provider>
  );
}
